'use client';

import { PanelToggleButton } from '@heidi/design-system/panel-toggle-button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { useIntl } from 'react-intl';

import { useAgentHomePanel } from './agent-home-panel-state';
import { navV3Messages } from './nav-v3.messages';

export interface AgentHomePanelToggleProps {
  /** Render in the panel header while open, or on the Home canvas while closed. */
  placement?: 'page' | 'panel';
}

/** One Home-only toggle that hands off between the panel header and Home canvas. */
export const AgentHomePanelToggle = ({
  placement = 'page',
}: AgentHomePanelToggleProps): JSX.Element | null => {
  const intl = useIntl();
  const { isOpen, toggle } = useAgentHomePanel();
  const belongsInPanel = placement === 'panel';

  if (isOpen !== belongsInPanel) return null;

  const label = intl.formatMessage(
    isOpen ? navV3Messages.hideHomeNavigation : navV3Messages.showHomeNavigation
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <PanelToggleButton
            open={isOpen}
            side="left"
            variant={belongsInPanel ? 'panel_ghost' : 'ghost'}
            size="sm"
            aria-label={label}
            aria-controls="agent-home-panel"
            data-testid="agent-home-panel-toggle"
            onClick={toggle}
          />
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <TooltipArrow />
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
